'use client';

import React, { useState, useEffect, useRef } from 'react';
import AdminNavbar from '../navbar/AdminNavbar';
import NavItem from '../component/NavItem';
import styles from './AdminLayout.module.css';
import { InnocodeButton, InnocodeFooter, InnocodeList, useDialog, useInnocodeLoadingOverlay, useInnocodeOverlayMenu } from '@intsoft/innocode-core';
import { useRouter, usePathname } from 'next/navigation';

export enum Platform {
    // 1920px 이상
    ADMINWEB = 'ADMINWEB',
    // 1440px ~ 1920px 미만
    WEB = 'WEB',
    // 768px ~ 1440px 미만
    TABLET = 'TABLET',
    // 375px ~ 768px 미만
    MOBILE = 'MOBILE',
}

export interface AdminLayoutProps {
    children: React.ReactNode;
    enableNavbar?: boolean;
    enableHeaderWorkspaceInfo?: boolean;
    enableHeaderMenuButton?: boolean;
    enableHeaderGoBackButton?: boolean;
    enablePlanMoveButton?: boolean;
}

export default function AdminLayout({ children, enableNavbar = true, enableHeaderWorkspaceInfo = true, enableHeaderMenuButton = true, enableHeaderGoBackButton = false, enablePlanMoveButton = true }: AdminLayoutProps) {
    const router = useRouter();
    const currentPageUrl = usePathname() ?? '';
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [platform, setPlatform] = useState<Platform>(Platform.ADMINWEB);  
    const profileRef = useRef<HTMLDivElement>(null);

    const toggleMobileMenu = () => {
        setIsMobileMenuOpen(!isMobileMenuOpen);
    };

    const closeMobileMenu = () => {
        setIsMobileMenuOpen(false);
    };

    const getPlatform = (width: number): Platform => {
        if (width >= 1440) return Platform.ADMINWEB;
        if (width >= 1024) return Platform.WEB;
        if (width >= 768) return Platform.TABLET;
        return Platform.MOBILE;
    }; 

    useEffect(() => {
        const handleResize = () => {
            const body = document.body;
            body.setAttribute('data-theme-typography', 'KR');
            body.setAttribute('data-theme', 'light');

            const width = window.innerWidth;

            // Platform 상태 업데이트
            setPlatform(getPlatform(width));

            // 기존 data-theme-grid 설정
            if (width <= 375) {
                body.setAttribute('data-theme-grid', 'mobile');
            } else if (width <= 768) {
                body.setAttribute('data-theme-grid', 'tablet-vertical');
            } else {
                body.setAttribute('data-theme-grid', 'web');
            }
        };

        // 초기 실행
        handleResize();

        // resize 이벤트 리스너 등록
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);
 
    const isMenuActive = (slug: string) => {
        if (slug === '/') return currentPageUrl === '/';
        return currentPageUrl === slug || currentPageUrl.startsWith(slug + '/');
    }; 

    return (
        <div className="innocode-breakpoint-layout-bg" style={{ backgroundColor: 'white' }}>
            <div className="innocode-breakpoint-content-admin" >
                <div className={styles.layoutContainer}>
                    {/* Mobile Header - Mobile Only */}
                    {/* {((platform === "MOBILE") || (platform === "TABLET") ) && ( */}
                        <header className={styles.header}>
                            <div className={styles.headerContent}>
                                <div className={styles.headerLeft}>
                                    {(platform === Platform.ADMINWEB || platform === Platform.WEB  || platform === Platform.TABLET) && (
                                        <a href="/">
                                            <svg width="156" height="59" viewBox="0 0 156 59" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M17.6191 32.7452C17.6191 27.7628 21.0911 23.9932 25.6775 23.9932C27.7083 23.9932 29.4451 24.7474 30.7221 26.0577V24.3866H35.5051V35.138C35.5051 35.9899 35.9958 36.4824 36.8486 36.4824H37.1768V41.1039H35.8332C33.5408 41.1039 32.0331 40.3496 31.2793 38.8427C29.9357 40.4813 28.004 41.4972 25.6775 41.4972C21.0911 41.4972 17.6191 37.7276 17.6191 32.7452ZM30.7221 32.7452C30.7221 30.3849 28.9853 28.6147 26.6263 28.6147C24.2674 28.6147 22.5647 30.3849 22.5647 32.7452C22.5647 35.1055 24.3014 36.8757 26.6263 36.8757C28.9513 36.8757 30.7221 35.1055 30.7221 32.7452Z" fill="#3781FB"/>
                                                <path d="M38.7812 32.7458C38.7812 27.7635 42.2532 23.9938 46.8396 23.9938C48.8704 23.9938 50.6072 24.748 51.8842 26.0583V17.5029H56.6672V35.1371C56.6672 35.9889 57.1579 36.4814 58.0108 36.4814H58.3389V41.1029H56.9953C54.7029 41.1029 53.1953 40.3487 52.4414 38.8417C51.0979 40.4803 49.1661 41.4963 46.8396 41.4963C42.2532 41.4963 38.7812 37.7266 38.7812 32.7443V32.7458ZM51.8857 32.7458C51.8857 30.3855 50.149 28.6153 47.79 28.6153C45.431 28.6153 43.7283 30.3855 43.7283 32.7458C43.7283 35.1061 45.4651 36.8764 47.79 36.8764C50.1149 36.8764 51.8857 35.1061 51.8857 32.7458Z" fill="#3781FB"/>
                                                <path d="M60.0098 32.7458C60.0098 27.7635 63.4817 23.9938 68.0681 23.9938C70.0989 23.9938 71.8357 24.748 73.1127 26.0583V17.5029H77.8957V35.1371C77.8957 35.9889 78.3864 36.4814 79.2393 36.4814H79.5674V41.1029H78.2239C75.9314 41.1029 74.4238 40.3487 73.6699 38.8417C72.3264 40.4803 70.3946 41.4963 68.0681 41.4963C63.4817 41.4963 60.0098 37.7266 60.0098 32.7443V32.7458ZM73.1142 32.7458C73.1142 30.3855 71.3775 28.6153 69.0185 28.6153C66.6595 28.6153 64.9568 30.3855 64.9568 32.7458C64.9568 35.1061 66.6936 36.8764 69.0185 36.8764C71.3435 36.8764 73.1142 35.1061 73.1142 32.7458Z" fill="#3781FB"/>
                                                <path d="M81.9258 35.2033V24.3867H86.7088V33.7614C86.7088 35.6292 87.8883 36.8759 89.69 36.8759C91.6233 36.8759 92.9329 35.5982 92.9329 33.6963V24.3867H97.7159V35.1382C97.7159 35.99 98.2065 36.4825 99.0594 36.4825H99.3876V41.104H98.044C95.75 41.104 94.2114 40.3498 93.4901 38.8428C92.179 40.5805 90.3448 41.4974 88.0849 41.4974C84.3823 41.4974 81.9258 39.007 81.9258 35.2048V35.2033Z" fill="#3781FB"/>
                                                <path d="M100.926 32.7452C100.926 27.7628 104.791 23.9932 109.935 23.9932C114.422 23.9932 117.993 26.911 118.55 30.975H113.473C113.015 29.5656 111.606 28.6147 109.935 28.6147C107.576 28.6147 105.839 30.3849 105.839 32.7452C105.839 35.1055 107.576 36.8757 109.935 36.8757C111.605 36.8757 113.013 35.9248 113.473 34.5154H118.55C117.993 38.5794 114.422 41.4972 109.935 41.4972C104.791 41.4972 100.926 37.7276 100.926 32.7452Z" fill="#3781FB"/>
                                                <path d="M120.351 32.7452C120.351 27.7628 124.216 23.9932 129.36 23.9932C134.504 23.9932 138.369 27.7303 138.369 32.8428C138.369 33.3012 138.336 33.8588 138.237 34.2847H125.461C125.985 36.185 127.524 37.3992 129.522 37.3992C131.063 37.3992 132.307 36.7441 132.994 35.8257H138.106C136.861 39.202 133.519 41.4957 129.391 41.4957C124.215 41.4957 120.35 37.726 120.35 32.7437L120.351 32.7452ZM125.495 31.1066H133.259C132.768 29.2713 131.261 28.0912 129.361 28.0912C127.462 28.0912 126.02 29.2713 125.496 31.1066H125.495Z" fill="#3781FB"/>
                                            </svg>
                                        </a>
                                    )} 

                                    {(platform === Platform.MOBILE) && (
                                        <InnocodeButton enablePadding={false} onClick={toggleMobileMenu} designType={'assistive'} theme={'opacity'} iconOnly={true} icon={
                                            <svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                                                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 17h14M5 12h14M5 7h14" />
                                            </svg>
                                        }></InnocodeButton>
                                    )} 
                                    {(platform === Platform.ADMINWEB || platform === Platform.WEB || platform === Platform.TABLET) && (
                                        <div className={styles.menuWrapper}>
                                            <NavItem label="서비스 소개" href="/" match="exact" status={isMenuActive("/") ? "pressed" : "default"}/>
                                            <NavItem label="사업 소개" href="/about" match="exact" status={isMenuActive("/about") ? "pressed" : "default"}/>
                                            <NavItem label="상품 리스트" href="/products" match="exact" status={isMenuActive("/products") ? "pressed" : "default"}/>
                                            <NavItem label="고객 문의" href="/contact" match="exact" status={isMenuActive("/contact") ? "pressed" : "default"}/>
                                        </div>
                                    )}
                                </div>
                                {/* {(platform === Platform.ADMINWEB || platform === Platform.WEB) && (
                                    <div className={styles.menuWrapper}>
                                        <NavItem label="서비스 소개" href="/" match="exact" status={isMenuActive("/") ? "pressed" : "default"}/>
                                        <NavItem label="사업 소개" href="/about" match="exact" status={isMenuActive("/about") ? "pressed" : "default"}/>
                                        <NavItem label="상품 리스트" href="/products" match="exact" status={isMenuActive("/products") ? "pressed" : "default"}/>
                                        <NavItem label="고객 문의" href="/contact" match="exact" status={isMenuActive("/contact") ? "pressed" : "default"}/>
                                    </div>
                                )}  */}
                                <div className={styles.headerRight}>  
                                    <a target="_blank" href="https://admin.adduce.co.kr">
                                    <InnocodeButton
                                            size={"sm"}
                                            designType="primary"
                                            theme="line"   
                                        >관리도구</InnocodeButton>
                                    </a> 
                                </div>

                            </div>
                            
                        </header>
                    {/* )} */}

                    {/* Mobile Menu Overlay */}
                    {((platform === Platform.MOBILE) && isMobileMenuOpen) && (
                        <div className={styles.mobileMenuOverlay} onClick={closeMobileMenu}></div>
                    )}
                    {(enableNavbar) ? (
                        <div className={styles.adminLayoutWrapper}>
                            {(platform === Platform.WEB || platform === Platform.ADMINWEB || platform === Platform.TABLET) && (
                                <div className={styles.navbarContainer}>
                                    <AdminNavbar platform={platform} currentPageUrl={currentPageUrl} />
                                </div>
                            )}
                            {((platform === Platform.MOBILE) && isMobileMenuOpen) && (
                                <div className={`${styles.navbarContainer} ${styles.mobileOpen}`}>
                                    <AdminNavbar platform={platform} currentPageUrl={currentPageUrl} closeMobileMenu={closeMobileMenu} />
                                </div>
                            )}
                            <div className={styles.contentArea}>
                                {React.isValidElement(children)
                                    ? React.cloneElement(children, { platform } as any)
                                    : children
                                }
                                <InnocodeFooter />
                            </div>
                        </div>
                    ) : (
                        <div className={styles.adminLayoutWrapper}>
                            <div className={styles.contentArea}>
                                {React.isValidElement(children)
                                    ? React.cloneElement(children, { platform } as any)
                                    : children
                                }
                                <InnocodeFooter />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
